/* ── Retenções, vencimento e corpo da nota (informações complementares) ──
   Tudo sai do perfil de tributação: % de cada retenção, regra de
   vencimento, dados de pagamento e texto fixo. Funções puras -- a tela só
   mostra e manda no pedido. */

const arred = (v) => Math.round((Number(v) || 0) * 100) / 100;
export const moedaBr = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const pct = (v) => String(Number(v)).replace('.', ',');

/* valores adicionais do tomador (até 3, demonstrados à parte no corpo) */
export const adicionaisValidos = (dados) => (dados.adicionais || [])
  .map(a => ({ rotulo: String(a.rotulo || '').trim(), valor: Number(a.valor || 0) }))
  .filter(a => a.valor > 0);
/* valor total do serviço = base + adicionais (é o que vai na nota) */
export const valorTotalServico = (dados) => arred(Number(dados.valorServico || 0) + adicionaisValidos(dados).reduce((t, a) => t + a.valor, 0));

/* vencimento pela regra do perfil: dia fixo do mês seguinte ou N dias após a
   emissão (AAAA-MM-DD). Dia 31 em mês curto vira o último dia. */
export function calcularVencimento(tipo, valor, emissaoIso) {
  const n = Number(valor);
  /* "informar": a data é digitada na emissão, não há regra */
  if (!tipo || tipo === 'informar' || !(n > 0)) return '';
  const [a, m, d] = String(emissaoIso || '').slice(0, 10).split('-').map(Number);
  if (!a) return '';
  let dt;
  if (tipo === 'dias_apos') dt = new Date(Date.UTC(a, m - 1, d + n));
  else {
    /* dia_fixo_mes: no mês da nota; dia_fixo: no mês seguinte */
    const mes = tipo === 'dia_fixo_mes' ? m - 1 : m;
    const ultimo = new Date(Date.UTC(a, mes + 1, 0)).getUTCDate();
    dt = new Date(Date.UTC(a, mes, Math.min(n, ultimo)));
    /* sábado/domingo antecipa pra sexta; se a sexta cair no mês anterior
       (dia 1 no fim de semana), vai pra segunda -- vencimento não sai do mês */
    const semana = dt.getUTCDay();
    if (semana === 6 || semana === 0) {
      const antes = new Date(dt.getTime() - (semana === 6 ? 1 : 2) * 86400000);
      dt = antes.getUTCMonth() === dt.getUTCMonth() ? antes : new Date(dt.getTime() + (semana === 6 ? 2 : 1) * 86400000);
    }
  }
  return dt.toISOString().slice(0, 10);
}

/* retenções da nota: ISS (se retido) e as federais pelos % do perfil; valor
   digitado à mão nas opções de tributação federal vence o % */
export function calcularRetencoes(dados) {
  const valor = valorTotalServico(dados);
  if (!(valor > 0)) return [];
  const base = Math.max(0, valor - Number(dados.descontoIncondicionado || 0));
  const itens = [];
  const add = (chave, rotulo, valorRet, origem) => { if (valorRet > 0) itens.push({ chave, rotulo, valor: arred(valorRet), origem }); };
  if (dados.reterIss) {
    const aliq = Number(dados.aliquotaIss || 0);
    if (aliq > 0) add('iss', 'ISS retido', base * aliq / 100, `${pct(aliq)}%`);
  }
  const porPct = (campo) => Number(dados[campo] || 0);
  const manualIr = Number(dados.irrf || 0);
  add('irrf', 'IRRF', manualIr > 0 ? manualIr : base * porPct('retIrrf') / 100, manualIr > 0 ? 'informado' : `${pct(porPct('retIrrf'))}%`);
  const manualInss = Number(dados.contribuicaoPrevidenciaria || 0);
  add('inss', 'INSS', manualInss > 0 ? manualInss : base * porPct('retInss') / 100, manualInss > 0 ? 'informado' : `${pct(porPct('retInss'))}%`);
  add('pis', 'PIS', base * porPct('retPis') / 100, `${pct(porPct('retPis'))}%`);
  add('cofins', 'COFINS', base * porPct('retCofins') / 100, `${pct(porPct('retCofins'))}%`);
  add('csll', 'CSLL', base * porPct('retCsll') / 100, `${pct(porPct('retCsll'))}%`);
  return itens;
}

const dataBr = (iso) => (iso ? iso.split('-').reverse().join('/') : '');

/* corpo da nota: retenções + líquido, pagamento, vencimento, texto fixo do
   perfil e o que a pessoa escreveu em "informações complementares" */
/* formato do corpo para quem tem retenção (o mesmo do sistema de cálculo que a
   equipe usava): rótulos com pontilhado até a coluna 40 e o valor em R$ */
/* sempre com pelo menos 3 pontos: rótulo com mais de 40 letras (adicional
   comprido) saía sem pontilhado e o PDF não reconhecia a linha como valor */
const linha = (rotulo, valor, semDoisPontos) => `${(semDoisPontos ? rotulo : `${rotulo}:`).padEnd(40, '.').replace(/([^.])$/, '$1...').replace(/([^.])\.{1,2}$/, '$1...')} R$ ${Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pctPonto = (v) => `${Number(v || 0).toFixed(2)}%`;

export function montarCorpoRetencao(dados, retencoes) {
  /* enxuto pra caber numa folha (pedido de 03/11/2026): a descrição abre o
     texto sem o título "DESCRIÇÃO DOS SERVIÇOS" (o campo da nota já tem),
     base só aparece quando há adicionais, cada retenção diz "RETIDO" na
     própria linha e o pagamento vai numa linha só, separado por barra. A
     1ª linha ser a descrição é o que o backend usa pra pôr este texto no
     lugar da descrição (services/nfse/dps.js). */
  const linhas = [];
  const desc = String(dados.descricaoServico || '').trim();
  if (desc) linhas.push(desc.toUpperCase());
  const total = valorTotalServico(dados);
  const adicionais = adicionaisValidos(dados);
  if (adicionais.length) {
    linhas.push(linha('VALOR DO SERVIÇO BASE', dados.valorServico));
    adicionais.forEach(a => linhas.push(linha((a.rotulo || 'VALOR ADICIONAL').toUpperCase(), a.valor)));
  }
  linhas.push(linha('VALOR TOTAL DOS SERVIÇOS', total));
  if (retencoes.length) {
    const aliq = { iss: dados.aliquotaIss, irrf: dados.retIrrf, inss: dados.retInss, pis: dados.retPis, cofins: dados.retCofins, csll: dados.retCsll };
    retencoes.forEach(r => {
      const nome = { iss: 'ISS', irrf: 'IRRF', inss: 'INSS', pis: 'PIS', cofins: 'COFINS', csll: 'CSLL' }[r.chave] || r.rotulo;
      linhas.push(r.origem === 'informado' ? linha(`${nome} RETIDO`, r.valor) : linha(`${nome} RETIDO ${pctPonto(aliq[r.chave])}`, r.valor, true));
    });
    const totalRet = arred(retencoes.reduce((t, r) => t + r.valor, 0));
    linhas.push(linha('TOTAL DAS RETENÇÕES', totalRet));
    linhas.push(linha('VALOR LÍQUIDO A RECEBER', arred(total - Number(dados.descontoIncondicionado || 0) - totalRet)));
  }
  const pag = String(dados.dadosPagamento || '').split('\n').map(l => l.trim()).filter(Boolean).join(' | ');
  if (pag) linhas.push(`DADOS DE PAGAMENTO: ${pag}`);
  if (dados.vencimento) linhas.push(`VENCIMENTO: ${dataBr(dados.vencimento)}`);
  if (String(dados.textoComplementar || '').trim()) linhas.push(dados.textoComplementar.trim());
  if (String(dados.informacoesComplementares || '').trim()) linhas.push(dados.informacoesComplementares.trim());
  return linhas.join('\n').slice(0, 2000);
}

export function montarCorpo(dados, retencoes) {
  if (dados.formatoRetencao) return montarCorpoRetencao(dados, retencoes);
  const partes = [];
  const valor = valorTotalServico(dados);
  if (retencoes.length && valor > 0) {
    const total = retencoes.reduce((t, r) => t + r.valor, 0);
    const liquido = arred(valor - Number(dados.descontoIncondicionado || 0) - total);
    partes.push(`Retenções: ${retencoes.map(r => `${r.rotulo.replace(' retido', '')} ${moedaBr(r.valor)} (${r.origem})`).join('; ')}. Total retido ${moedaBr(total)}. Valor líquido a receber ${moedaBr(liquido)}.`);
  }
  if (dados.vencimento) partes.push(`Vencimento: ${dataBr(dados.vencimento)}.`);
  if (String(dados.dadosPagamento || '').trim()) partes.push(`Pagamento: ${dados.dadosPagamento.trim()}`);
  if (String(dados.textoComplementar || '').trim()) partes.push(dados.textoComplementar.trim());
  if (String(dados.informacoesComplementares || '').trim()) partes.push(dados.informacoesComplementares.trim());
  return partes.join('\n').slice(0, 2000);
}

/* campos de retenção do pedido (nomes de services/nfse/dps.js) */
export function valoresRetencao(dados, retencoes, cstPisCofins) {
  const v = (chave) => retencoes.find(r => r.chave === chave)?.valor || 0;
  const federais = ['irrf', 'inss', 'pis', 'cofins', 'csll'].some(k => v(k) > 0);
  if (!federais) return {};
  const base = arred(Math.max(0, valorTotalServico(dados) - Number(dados.descontoIncondicionado || 0)));
  const temPisCofins = v('pis') > 0 || v('cofins') > 0;
  return {
    possui_retencao: true,
    valor_retencao_irrf: v('irrf') || undefined,
    valor_retencao_inss: v('inss') || undefined,
    valor_retencao_csll: v('csll') || undefined,
    ...(temPisCofins ? {
      pis_cofins_cst: cstPisCofins || '01',
      pis_cofins_base: base,
      pis_cofins_aliq_pis: Number(dados.retPis || 0) || undefined,
      pis_cofins_aliq_cofins: Number(dados.retCofins || 0) || undefined,
      valor_pis: v('pis') || undefined,
      valor_cofins: v('cofins') || undefined,
      tipo_retencao_pis_cofins: 1,
    } : {}),
  };
}
